/**
 * Backoffice CRM: the live view of the catalog.
 *
 * Listeners on globals/admin_catalog_summary (with a fallback on globals/catalog_summary while the
 * admin summary has no recipes), globals/master_ingredients, globals/categories and
 * globals/coverage_targets. app.js starts them once onAuthStateChanged has let an admin in and
 * stops them, through the function returned, when it reports nobody or a non-admin (#1395): a
 * listener that outlived the sign-out asked Firestore for its document again, signed out, and a
 * second sign-in on the same page started a second set, which rendered every snapshot twice.
 */
export function listenToCatalog({ db, doc, onSnapshot }, { onRecipes, onIngredients, onCategories, onCoverageTargets }) {
  let stopCatalogFallback = null;

  const listenToCatalogFallback = () => {
    if (stopCatalogFallback) return;
    stopCatalogFallback = onSnapshot(doc(db, "globals", "catalog_summary"), (snap) => {
      if (snap.exists()) onRecipes(snap.data().recipes || {});
    }, (err) => console.warn("Catalog summary fallback listener error:", err.message));
  };

  const stops = [
    onSnapshot(doc(db, "globals", "admin_catalog_summary"), (snap) => {
      if (snap.exists() && snap.data() && snap.data().recipes) {
        if (stopCatalogFallback) {
          stopCatalogFallback();
          stopCatalogFallback = null;
        }
        onRecipes(snap.data().recipes || {});
      } else {
        listenToCatalogFallback();
      }
    }, (err) => {
      console.warn("Admin catalog summary listener error, falling back to catalog_summary:", err.message);
      listenToCatalogFallback();
    }),

    onSnapshot(doc(db, "globals", "master_ingredients"), (snap) => {
      if (snap.exists()) onIngredients(snap.data() || {});
    }, (err) => console.warn("Master ingredients listener error:", err.message)),

    onSnapshot(doc(db, "globals", "categories"), (snap) => {
      if (snap.exists()) onCategories(snap.data() || {});
    }, (err) => console.warn("Categories listener error:", err.message)),

    // The catalog goals recipe-builder's sync publishes (admin-only): null when missing or unreadable.
    onSnapshot(doc(db, "globals", "coverage_targets"), (snap) => {
      onCoverageTargets(snap.exists() ? snap.data() : null);
    }, (err) => {
      console.warn("Coverage targets listener error:", err.message);
      onCoverageTargets(null);
    })
  ];

  return function stopListeningToCatalog() {
    stops.forEach((stop) => stop());
    if (stopCatalogFallback) {
      stopCatalogFallback();
      stopCatalogFallback = null;
    }
  };
}
